import { getGetApplicationsUrl } from 'generatedTypes/flight/services/application-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type {
    ApplicationResponse,
    FetchApplications,
    GetApplicationsParams,
    PagedResponseApplicationResponse
} from './types';

const useApplications = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchApplications: FetchApplications = async requestOptions => {
        try {
            setIsLoading(true);

            const url = getGetApplicationsUrl(requestOptions);

            return await withRetry(
                () => authenticatedGet<PagedResponseApplicationResponse>(url),
                CONNECTION_ERROR,
                () => fetchApplications(requestOptions)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data,
        fetch: paginatedFetch,
        reset
    } = usePagination<ApplicationResponse>({
        callback: fetchApplications
    });

    const handleFetch = (args?: GetApplicationsParams) => {
        const {
            batteryId,
            boundaryId,
            clientId,
            droneId,
            name,
            pageSize = DEFAULT_PAGE_SIZE,
            parcelId,
            pilotId,
            scheduledPilotId,
            siteId,
            sort
        } = args || {};

        return paginatedFetch({
            batteryId,
            boundaryId,
            clientId,
            droneId,
            name,
            pageSize,
            parcelId,
            pilotId,
            scheduledPilotId,
            siteId,
            sort
        });
    };

    return {
        applications: data as ApplicationResponse[],
        fetch: handleFetch,
        isLoading,
        reset
    };
};

export default useApplications;
